import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type { Database } from '@/integrations/supabase/database.types';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
  createServiceError,
} from "@/services/core/service-registry";
import type {
  Season as GamificationSeason,
  UserSeasonProgress,
  CreateSeasonRequest,
  AwardXPRequest,
  LeaderboardFilters,
  GamificationSeasonStats,
  OrgGamificationProgress,
  ProjectGamificationProgress,
  OrgLeaderboardEntry,
  ProjectLeaderboardEntry,
  ScopedAwardXPRequest,
  ScopedAwardXPResult,
} from '@flowstate/types';
import type { LeaderboardEntry } from '@/types/gamification';
import { logger } from "@/lib/logger";

export type { CreateSeasonRequest, AwardXPRequest, LeaderboardFilters, GamificationSeasonStats as SeasonStats };

export class GamificationSeasonService implements IService {
  public readonly name: 'gamification' | 'gamification-v2';
  private readonly supabase = supabase;
  private initialized = false;

  constructor(name: 'gamification' | 'gamification-v2' = 'gamification') {
    this.name = name;
  }

  private buildMetadata(startTime: number) {
    return {
      duration: Date.now() - startTime,
      cached: false,
    };
  }

  private readonly CACHE_TTL = {
    season: 5 * 60 * 1000,
    leaderboard_day: 60 * 1000,
    leaderboard_week: 5 * 60 * 1000,
    leaderboard_month: 15 * 60 * 1000,
    leaderboard_season: 15 * 60 * 1000,
    user_progress: 30 * 1000,
  };

  async initialize(): Promise<void> {
    if (this.initialized) {return;}
    logger.info(`✅ ${this.name} initialized`);
    this.initialized = true;
    await this.ensureActiveSeasonExists();
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info(`✅ ${this.name} disposed`);
  }

  async getCurrentSeason(): Promise<ServiceResponse<GamificationSeason | null>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.supabase
        .from('gamification_seasons')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {throw error;}

      return createSuccessResponse(data as GamificationSeason | null, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'SEASON_FETCH_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch current season',
          this.name,
          { error }
        )
      );
    }
  }

  async createSeason(request: CreateSeasonRequest): Promise<ServiceResponse<GamificationSeason>> {
    const startTime = Date.now();

    try {
      const start_date = request.start_date ?? new Date().toISOString();
      const end_date = new Date(start_date);
      end_date.setDate(end_date.getDate() + request.duration_days);

      await this.supabase
        .from('gamification_seasons')
        .update({ is_active: false })
        .eq('is_active', true);

      const { data, error } = await this.supabase
        .from('gamification_seasons')
        .insert({
          season_name: request.season_name,
          season_description: request.season_description,
          duration_days: request.duration_days,
          start_date,
          end_date: end_date.toISOString(),
          is_active: true,
          rewards_config: (request.rewards_config ?? {
            top_10_badge: 'season_champion',
            top_25_badge: 'season_star',
            top_50_badge: 'season_participant',
          }) as Json,
        })
        .select()
        .single();

      if (error) {throw error;}

      return createSuccessResponse(data as GamificationSeason, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'SEASON_CREATE_FAILED',
          error instanceof Error ? error.message : 'Failed to create season',
          this.name,
          { error }
        )
      );
    }
  }

  async endCurrentSeasonAndCreateNext(
    nextSeasonName: string,
    nextSeasonDays: number = 90
  ): Promise<ServiceResponse<{ ended: GamificationSeason; created: GamificationSeason }>> {
    const startTime = Date.now();

    try {
      const currentSeasonResp = await this.getCurrentSeason();
      if (currentSeasonResp.error || !currentSeasonResp.data) {
        throw new Error('No active season to end');
      }

      const currentSeason = currentSeasonResp.data;

      const { error: endError } = await this.supabase
        .from('gamification_seasons')
        .update({
          is_active: false,
          is_completed: true,
        })
        .eq('id', currentSeason.id);

      if (endError) {throw endError;}

      await this.calculateSeasonRankings(currentSeason.id);

      const nextSeasonResp = await this.createSeason({
        season_name: nextSeasonName,
        duration_days: nextSeasonDays,
      });

      if (!nextSeasonResp.success || !nextSeasonResp.data) {
        throw new Error(
          !nextSeasonResp.success ? nextSeasonResp.error : 'Failed to create next season'
        );
      }

      return createSuccessResponse(
        {
          ended: { ...currentSeason, is_active: false, is_completed: true },
          created: nextSeasonResp.data,
        },
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'SEASON_TRANSITION_FAILED',
          error instanceof Error ? error.message : 'Failed to end season and create next',
          this.name,
          { error }
        )
      );
    }
  }

  private async ensureActiveSeasonExists(): Promise<void> {
    const currentSeasonResp = await this.getCurrentSeason();

    if (!currentSeasonResp.data) {
      logger.info('⚠️ No active season found. Creating default season...');

      const { count } = await this.supabase
        .from('gamification_seasons')
        .select('*', { count: 'exact', head: true });

      const seasonNumber = (count ?? 0) + 1;

      await this.createSeason({
        season_name: `Season ${seasonNumber}`,
        season_description: 'Automatically created season',
        duration_days: 90,
      });

      logger.info(`✅ Created Season ${seasonNumber} (90 days)`);
    }
  }

  async awardXP(request: AwardXPRequest): Promise<ServiceResponse<{
    main_xp_awarded: boolean;
    season_xp_awarded: boolean;
    new_total_xp: number;
    new_season_xp: number;
    season_level: number;
  }>> {
    const startTime = Date.now();

    try {
      if (request.xp_amount <= 0) {
        throw new Error('XP amount must be positive');
      }

      const { error: mainError } = await this.supabase
        .from('point_transactions')
        .insert({
          user_id: request.user_id,
          task_id: request.task_id,
          xp_awarded: request.xp_amount,
          transaction_type: request.transaction_type,
          details: request.details as Json | undefined,
        });

      if (mainError) {throw mainError;}

      const seasonResp = await this.getCurrentSeason();
      let seasonXPAwarded = false;
      let newSeasonXP = 0;
      let seasonLevel = 0;

      if (seasonResp.data) {
        const season = seasonResp.data;

        const { error: seasonError } = await this.supabase
          .from('season_xp_transactions')
          .insert({
            user_id: request.user_id,
            season_id: season.id,
            xp_amount: request.xp_amount,
            transaction_type: request.transaction_type,
            task_id: request.task_id,
            details: request.details as Json | undefined,
          });

        if (seasonError) {throw seasonError;}

        const { data: progress, error: progressError } = await this.supabase
          .from('user_season_progress')
          .select('*')
          .eq('user_id', request.user_id)
          .eq('season_id', season.id)
          .maybeSingle();

        if (progressError) {throw progressError;}

        const currentXP = progress?.season_xp ?? 0;
        newSeasonXP = currentXP + request.xp_amount;
        seasonLevel = this.calculateLevel(newSeasonXP);

        if (progress) {
          await this.supabase
            .from('user_season_progress')
            .update({
              season_xp: newSeasonXP,
              season_level: seasonLevel,
              tasks_completed: (progress.tasks_completed ?? 0) + (request.task_id ? 1 : 0),
              last_activity_at: new Date().toISOString(),
            })
            .eq('id', progress.id);
        } else {
          await this.supabase
            .from('user_season_progress')
            .insert({
              user_id: request.user_id,
              season_id: season.id,
              season_xp: newSeasonXP,
              season_level: seasonLevel,
              tasks_completed: request.task_id ? 1 : 0,
              first_activity_at: new Date().toISOString(),
              last_activity_at: new Date().toISOString(),
            });
        }

        seasonXPAwarded = true;
      }

      const { data: profile } = await this.supabase
        .from('profiles')
        .select('xp')
        .eq('id', request.user_id)
        .single();

      return createSuccessResponse(
        {
          main_xp_awarded: true,
          season_xp_awarded: seasonXPAwarded,
          new_total_xp: profile?.xp ?? 0,
          new_season_xp: newSeasonXP,
          season_level: seasonLevel,
        },
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'XP_AWARD_FAILED',
          error instanceof Error ? error.message : 'Failed to award XP',
          this.name,
          { error }
        )
      );
    }
  }

  async getLeaderboard(filters: LeaderboardFilters): Promise<ServiceResponse<LeaderboardEntry[]>> {
    const startTime = Date.now();

    try {
      const limit = filters.limit ?? 100;

      if (filters.period === 'season') {
        const season_id = filters.season_id ?? (await this.getCurrentSeason()).data?.id;
        if (!season_id) {
          return createSuccessResponse([], this.buildMetadata(startTime));
        }

        const { data, error } = await this.supabase
          .from('season_rankings')
          .select('*')
          .eq('season_id', season_id)
          .order('rank', { ascending: true })
          .limit(limit);

        if (error) {throw error;}

        const seasonEntries: LeaderboardEntry[] = (data ?? []).map((entry) => ({
          user_id: entry.user_id ?? '',
          username: entry.username ?? 'Unknown',
          full_name: entry.username ?? 'Unknown',
          display_name: entry.username ?? 'Unknown',
          avatar_url: entry.avatar_url ?? '',
          xp: entry.season_xp,
          xp_this_week: 0,
          xp_this_month: 0,
          current_streak: 0,
          longest_streak: 0,
          tasks_completed: 0,
          rank: entry.rank,
        }));

        return createSuccessResponse(seasonEntries, this.buildMetadata(startTime));
      }

      const now = new Date();
      let startDate = new Date();

      switch (filters.period) {
        case 'day':
          startDate.setDate(now.getDate() - 1);
          break;
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'all_time':
          startDate = new Date('2020-01-01');
          break;
      }

      const { data, error } = await this.supabase
        .from('point_transactions')
        .select('user_id, xp_awarded')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {throw error;}

      const userXP = new Map<string, number>();
      data.forEach((tx) => {
        if (!tx.user_id) {return;}
        const current = userXP.get(tx.user_id) ?? 0;
        userXP.set(tx.user_id, current + tx.xp_awarded);
      });

      const userIds = Array.from(userXP.keys());
      const { data: profiles } = await this.supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url')
        .in('id', userIds);

      const entries: LeaderboardEntry[] = [];
      userXP.forEach((xp, userId) => {
        const profile = profiles?.find((p) => p.id === userId);
        if (profile) {
          entries.push({
            user_id: userId,
            full_name: (profile.full_name ?? profile.username) ?? 'Unknown',
            display_name: (profile.full_name ?? profile.username) ?? 'Unknown',
            username: (profile.username ?? profile.full_name) ?? 'Unknown',
            avatar_url: profile.avatar_url ?? '',
            xp,
            xp_this_week: filters.period === 'week' ? xp : 0,
            xp_this_month: filters.period === 'month' ? xp : 0,
            current_streak: 0,
            longest_streak: 0,
            tasks_completed: 0,
            rank: 0,
          });
        }
      });

      entries.sort((a, b) => b.xp - a.xp);
      entries.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      return createSuccessResponse(entries.slice(0, limit), this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'LEADERBOARD_FETCH_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch leaderboard',
          this.name,
          { error, filters }
        )
      );
    }
  }

  async getUserProgress(userId: string, seasonId?: string): Promise<ServiceResponse<UserSeasonProgress | null>> {
    const startTime = Date.now();

    try {
      const season_id = seasonId ?? (await this.getCurrentSeason()).data?.id;
      if (!season_id) {
        return createSuccessResponse(null, this.buildMetadata(startTime));
      }

      const { data, error } = await this.supabase
        .from('user_season_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('season_id', season_id)
        .maybeSingle();

      if (error) {throw error;}

      return createSuccessResponse(data as UserSeasonProgress | null, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'PROGRESS_FETCH_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch user progress',
          this.name,
          { error, userId, seasonId }
        )
      );
    }
  }

  async calculateSeasonRankings(seasonId: string): Promise<ServiceResponse<number>> {
    const startTime = Date.now();

    try {
      const { data: progressData, error } = await this.supabase
        .from('user_season_progress')
        .select('user_id, season_xp, season_level')
        .eq('season_id', seasonId)
        .order('season_xp', { ascending: false });

      if (error) {throw error;}
      const safeProgressData = progressData ?? [];

      await this.supabase
        .from('season_rankings')
        .delete()
        .eq('season_id', seasonId);

      const rankings = await Promise.all(safeProgressData.map(async (progress, index) => {
        if (!progress.user_id) {
          return null;
        }

        const { data: profile } = await this.supabase
          .from('profiles')
          .select('username, full_name, avatar_url')
          .eq('id', progress.user_id)
          .single();

        return {
          season_id: seasonId,
          user_id: progress.user_id,
          rank: index + 1,
          season_xp: progress.season_xp ?? 0,
          season_level: progress.season_level ?? 0,
          username: (profile?.username ?? profile?.full_name) ?? 'Unknown',
          avatar_url: profile?.avatar_url,
        };
      }));
      const validRankings = rankings.filter((ranking): ranking is NonNullable<typeof ranking> => ranking !== null);

      if (validRankings.length > 0) {
        const { error: insertError } = await this.supabase
          .from('season_rankings')
          .insert(validRankings);

        if (insertError) {throw insertError;}
      }

      return createSuccessResponse(validRankings.length, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'RANKINGS_CALC_FAILED',
          error instanceof Error ? error.message : 'Failed to calculate rankings',
          this.name,
          { error, seasonId }
        )
      );
    }
  }

  async backfillSeasonData(seasonId: string): Promise<ServiceResponse<{
    transactions_created: number;
    users_updated: number;
  }>> {
    const startTime = Date.now();

    try {
      const { data: season } = await this.supabase
        .from('gamification_seasons')
        .select('start_date, end_date')
        .eq('id', seasonId)
        .single();

      if (!season) {throw new Error('Season not found');}

      const { data: transactions } = await this.supabase
        .from('point_transactions')
        .select('*')
        .gte('created_at', season.start_date)
        .lte('created_at', season.end_date || new Date().toISOString());

      if (!transactions || transactions.length === 0) {
        return createSuccessResponse({ transactions_created: 0, users_updated: 0 }, this.buildMetadata(startTime));
      }

      const seasonTransactions = transactions.map((tx) => ({
        user_id: tx.user_id,
        season_id: seasonId,
        xp_amount: tx.xp_awarded,
        transaction_type: tx.transaction_type,
        task_id: tx.task_id,
        details: tx.details,
        created_at: tx.created_at,
      }));

      const { error: insertError } = await this.supabase
        .from('season_xp_transactions')
        .insert(seasonTransactions);

      if (insertError) {throw insertError;}

      const userXP = new Map<string, number>();
      transactions.forEach((tx) => {
        if (!tx.user_id) {return;}
        const current = userXP.get(tx.user_id) ?? 0;
        userXP.set(tx.user_id, current + tx.xp_awarded);
      });

      for (const [userId, totalXP] of userXP.entries()) {
        const level = this.calculateLevel(totalXP);

        await this.supabase
          .from('user_season_progress')
          .upsert({
            user_id: userId,
            season_id: seasonId,
            season_xp: totalXP,
            season_level: level,
          }, {
            onConflict: 'user_id,season_id',
          });
      }

      return createSuccessResponse({
        transactions_created: seasonTransactions.length,
        users_updated: userXP.size,
      }, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'BACKFILL_FAILED',
          error instanceof Error ? error.message : 'Failed to backfill season data',
          this.name,
          { error, seasonId }
        )
      );
    }
  }

  private calculateLevel(xp: number): number {
    return Math.floor(Math.sqrt(xp / 100));
  }

  // ============================================================================
  // MULTI-SCOPE GAMIFICATION METHODS
  // ============================================================================

  // The new tables and RPCs are defined in migration 20260411000000 but not yet
  // reflected in the auto-generated Supabase types. We use typed wrappers here.
  private get untypedSupabase(): SupabaseClient<Database> {
    return this.supabase;
  }

  /**
   * Award XP across all applicable scopes (global, org, project, season)
   * Uses the `award_xp_scoped` RPC for atomic multi-table writes.
   */
  async awardXPScoped(request: ScopedAwardXPRequest): Promise<ServiceResponse<ScopedAwardXPResult>> {
    const startTime = Date.now();

    try {
      if (request.xp_amount <= 0) {
        throw new Error('XP amount must be positive');
      }

      const { data, error } = await this.untypedSupabase.rpc('award_xp_scoped', {
        p_user_id: request.user_id,
        p_organization_id: request.organization_id ?? undefined,
        p_project_id: request.project_id ?? undefined,
        p_xp_amount: request.xp_amount,
        p_transaction_type: request.transaction_type,
        p_task_id: request.task_id ?? undefined,
        p_details: (request.details ?? null) as Json,
      });

      if (error) { throw error; }

      const result = data as unknown as ScopedAwardXPResult;
      return createSuccessResponse(result, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'SCOPED_XP_AWARD_FAILED',
          error instanceof Error ? error.message : 'Failed to award scoped XP',
          this.name,
          { error }
        )
      );
    }
  }

  /**
   * Get organization-scoped leaderboard
   */
  async getOrgLeaderboard(
    organizationId: string,
    period: string = 'all_time',
    limit: number = 20
  ): Promise<ServiceResponse<OrgLeaderboardEntry[]>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase.rpc('get_org_leaderboard', {
        p_organization_id: organizationId,
        p_period: period,
        p_limit: limit,
      });

      if (error) { throw error; }

      return createSuccessResponse(
        (data as unknown as OrgLeaderboardEntry[]) || [],
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'ORG_LEADERBOARD_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch org leaderboard',
          this.name,
          { error, organizationId }
        )
      );
    }
  }

  /**
   * Get project-scoped leaderboard
   */
  async getProjectLeaderboard(
    projectId: string,
    limit: number = 20
  ): Promise<ServiceResponse<ProjectLeaderboardEntry[]>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase.rpc('get_project_leaderboard', {
        p_project_id: projectId,
        p_limit: limit,
      });

      if (error) { throw error; }

      return createSuccessResponse(
        (data as unknown as ProjectLeaderboardEntry[]) || [],
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'PROJECT_LEADERBOARD_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch project leaderboard',
          this.name,
          { error, projectId }
        )
      );
    }
  }

  /**
   * Get a user's organization gamification progress
   */
  async getOrgProgress(
    userId: string,
    organizationId: string
  ): Promise<ServiceResponse<OrgGamificationProgress | null>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase
        .from('organization_gamification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) { throw error; }

      return createSuccessResponse(
        data as OrgGamificationProgress | null,
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'ORG_PROGRESS_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch org progress',
          this.name,
          { error, userId, organizationId }
        )
      );
    }
  }

  /**
   * Get a user's project gamification progress
   */
  async getProjectProgress(
    userId: string,
    projectId: string
  ): Promise<ServiceResponse<ProjectGamificationProgress | null>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase
        .from('project_gamification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error) { throw error; }

      return createSuccessResponse(
        data as ProjectGamificationProgress | null,
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'PROJECT_PROGRESS_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch project progress',
          this.name,
          { error, userId, projectId }
        )
      );
    }
  }

  /**
   * Get the current active season for an organization
   */
  async getOrgCurrentSeason(organizationId: string): Promise<ServiceResponse<GamificationSeason | null>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase
        .from('gamification_seasons')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) { throw error; }

      return createSuccessResponse(data as GamificationSeason | null, this.buildMetadata(startTime));
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'ORG_SEASON_FETCH_FAILED',
          error instanceof Error ? error.message : 'Failed to fetch org season',
          this.name,
          { error, organizationId }
        )
      );
    }
  }

  /**
   * Create a new season for an organization
   */
  async createOrgSeason(
    organizationId: string,
    seasonName: string,
    durationDays: number = 90,
    rewardsConfig?: Record<string, unknown>
  ): Promise<ServiceResponse<{ season_id: string; season_name: string }>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase.rpc('create_org_season', {
        p_organization_id: organizationId,
        p_season_name: seasonName,
        p_duration_days: durationDays,
        p_rewards_config: (rewardsConfig ?? {
          top_10_badge: 'season_champion',
          top_25_badge: 'season_star',
          top_50_badge: 'season_participant',
        }) as Json,
      });

      if (error) { throw error; }

      const result = data as unknown as { success: boolean; season_id: string; season_name: string };
      if (!result.success) {
        throw new Error('Failed to create org season');
      }

      return createSuccessResponse(
        { season_id: result.season_id, season_name: result.season_name },
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'ORG_SEASON_CREATE_FAILED',
          error instanceof Error ? error.message : 'Failed to create org season',
          this.name,
          { error, organizationId }
        )
      );
    }
  }

  /**
   * Initialize gamification for a user joining an org
   */
  async initializeOrgGamification(
    userId: string,
    organizationId: string
  ): Promise<ServiceResponse<{ progress_id: string | null; season_id: string | null }>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.untypedSupabase.rpc('initialize_org_gamification', {
        p_user_id: userId,
        p_organization_id: organizationId,
      });

      if (error) { throw error; }

      const result = data as unknown as { success: boolean; progress_id: string | null; season_id: string | null };
      return createSuccessResponse(
        { progress_id: result.progress_id, season_id: result.season_id },
        this.buildMetadata(startTime)
      );
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'ORG_GAMIFICATION_INIT_FAILED',
          error instanceof Error ? error.message : 'Failed to initialize org gamification',
          this.name,
          { error, userId, organizationId }
        )
      );
    }
  }

  /**
   * Get Rizz transaction history for a user (admin only)
   */
  async getUserTransactionsAdmin(
    userId: string,
    limit: number = 500
  ): Promise<ServiceResponse<any[]>> {
    const startTime = Date.now();

    try {
      const { data, error } = await this.supabase
        .rpc("get_user_transactions_admin", {
          target_user_id: userId,
          max_rows: limit,
        })
        .order("created_at", { ascending: false });

      if (error) {
        logger.error("Error fetching XP transactions:", { error });
        return createErrorResponse(
          createServiceError(
            'USER_TRANSACTIONS_FETCH_FAILED',
            error.message,
            this.name,
            { userId, limit }
          )
        );
      }

      return createSuccessResponse((data as any[]) || [], this.buildMetadata(startTime));
    } catch (error) {
      logger.error("Exception fetching XP transactions:", { error });
      return createErrorResponse(
        createServiceError(
          'USER_TRANSACTIONS_FETCH_ERROR',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { userId, limit }
        )
      );
    }
  }
}

export const gamificationSeasonService = new GamificationSeasonService();
